import { __async, __objRest, __spreadProps, __spreadValues } from '../chunk-HIDP27A7.mjs';
import { UserRejectedRequestError, getAddress, SwitchChainError, numberToHex } from 'viem';
import { createConnector, normalizeChainId } from 'wagmi';

var APOLLO_WALLET_RDNS = "io.zeusx.apollowallet";
var ApolloWalletNotFoundError = class extends Error {
  constructor() {
    super("Apollo Wallet provider not found");
    this.name = "ApolloWalletNotFoundError";
  }
};
var isApolloWalletProvider = (provider) => Boolean(provider == null ? void 0 : provider.isApolloWallet);
function getApolloWalletProvider() {
  var _a;
  if (typeof window === "undefined") {
    return void 0;
  }
  if (window.apolloWallet && isApolloWalletProvider(window.apolloWallet)) {
    return window.apolloWallet;
  }
  if (window.ethereum && isApolloWalletProvider(window.ethereum)) {
    return window.ethereum;
  }
  if ((_a = window.ethereum) == null ? void 0 : _a.providers) {
    const provider = window.ethereum.providers.find(isApolloWalletProvider);
    if (provider) {
      return provider;
    }
  }
  return void 0;
}
function isApolloWalletInstalled() {
  return Boolean(getApolloWalletProvider());
}
function waitForApolloWalletProvider(timeoutMs = 2e3) {
  return __async(this, null, function* () {
    const provider = getApolloWalletProvider();
    if (provider) {
      return provider;
    }
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      yield new Promise((resolve) => {
        setTimeout(resolve, 100);
      });
      const found = getApolloWalletProvider();
      if (found) {
        return found;
      }
    }
    return getApolloWalletProvider();
  });
}
function wrapApolloProvider(provider) {
  const chainChangedHandlers = /* @__PURE__ */ new Map();
  return __spreadProps(__spreadValues({}, provider), {
    request: provider.request.bind(provider),
    on(event, handler) {
      if (event === "chainChanged") {
        const wrapped = (chainId) => handler(normalizeChainId(chainId));
        chainChangedHandlers.set(handler, wrapped);
        return provider.on(event, wrapped);
      }
      return provider.on(event, handler);
    },
    removeListener(event, handler) {
      if (event === "chainChanged") {
        const wrapped = chainChangedHandlers.get(handler);
        if (wrapped) {
          chainChangedHandlers.delete(handler);
          return provider.removeListener(event, wrapped);
        }
      }
      return provider.removeListener(event, handler);
    }
  });
}
function waitForChainId(provider, chainId, timeoutMs = 3e3) {
  return __async(this, null, function* () {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const chainIdResult2 = yield provider.request({ method: "eth_chainId" });
      const currentChainId = normalizeChainId(chainIdResult2);
      if (currentChainId === chainId) {
        return currentChainId;
      }
      yield new Promise((resolve) => {
        setTimeout(resolve, 200);
      });
    }
    const chainIdResult = yield provider.request({ method: "eth_chainId" });
    return normalizeChainId(chainIdResult);
  });
}
function initWalletProvider(provider, onAccountsChanged, onChainChanged, onDisconnect) {
  const wrapped = wrapApolloProvider(provider);
  wrapped.on("accountsChanged", onAccountsChanged);
  wrapped.on("chainChanged", onChainChanged);
  wrapped.on("disconnect", onDisconnect);
  return wrapped;
}
apolloWallet.type = "apollo";
function apolloWallet() {
  let walletProvider;
  const resetProvider = () => {
    walletProvider = void 0;
  };
  return createConnector((config) => ({
    id: "apollo",
    name: "Apollo Wallet",
    type: apolloWallet.type,
    connect() {
      return __async(this, arguments, function* ({ chainId } = {}) {
        try {
          const rawProvider = yield waitForApolloWalletProvider();
          if (!rawProvider) {
            throw new ApolloWalletNotFoundError();
          }
          if (!walletProvider) {
            walletProvider = initWalletProvider(
              rawProvider,
              this.onAccountsChanged.bind(this),
              this.onChainChanged.bind(this),
              this.onDisconnect.bind(this)
            );
          }
          const provider = walletProvider;
          config.emitter.emit("message", { type: "connecting" });
          yield provider.request({ method: "eth_requestAccounts" });
          const accounts = yield this.getAccounts();
          let currentChainId = yield this.getChainId();
          if (chainId && currentChainId !== chainId && this.switchChain) {
            try {
              const chain = yield this.switchChain({ chainId });
              currentChainId = chain.id;
            } catch (error) {
              if (error instanceof UserRejectedRequestError) {
                throw error;
              }
            }
          }
          return { accounts, chainId: currentChainId };
        } catch (error) {
          resetProvider();
          throw error;
        }
      });
    },
    disconnect() {
      return __async(this, null, function* () {
        const provider = walletProvider != null ? walletProvider : getApolloWalletProvider();
        if (!provider) {
          resetProvider();
          config.emitter.emit("disconnect");
          return;
        }
        try {
          yield provider.request({ method: "wallet_disconnect" });
        } catch (e) {
          yield provider.request({ method: "eth_disconnect" }).catch(() => void 0);
        }
        resetProvider();
        config.emitter.emit("disconnect");
      });
    },
    getAccounts() {
      return __async(this, null, function* () {
        const provider = walletProvider != null ? walletProvider : getApolloWalletProvider();
        if (!provider) {
          return [];
        }
        const accounts = yield provider.request({ method: "eth_accounts" });
        return accounts.map((account) => getAddress(account));
      });
    },
    getChainId() {
      return __async(this, null, function* () {
        const provider = walletProvider != null ? walletProvider : getApolloWalletProvider();
        if (!provider) {
          throw new ApolloWalletNotFoundError();
        }
        const chainId = yield provider.request({ method: "eth_chainId" });
        return normalizeChainId(chainId);
      });
    },
    getProvider() {
      return __async(this, null, function* () {
        if (!walletProvider) {
          const provider = getApolloWalletProvider();
          if (!provider) {
            throw new ApolloWalletNotFoundError();
          }
          walletProvider = initWalletProvider(
            provider,
            this.onAccountsChanged.bind(this),
            this.onChainChanged.bind(this),
            this.onDisconnect.bind(this)
          );
        }
        return walletProvider;
      });
    },
    isAuthorized() {
      return __async(this, null, function* () {
        try {
          const accounts = yield this.getAccounts();
          return accounts.length > 0;
        } catch (e) {
          return false;
        }
      });
    },
    switchChain(_0) {
      return __async(this, arguments, function* ({ chainId }) {
        var _a2, _c, _d;
        const provider = walletProvider != null ? walletProvider : getApolloWalletProvider();
        if (!provider) {
          throw new ApolloWalletNotFoundError();
        }
        const chain = config.chains.find((c) => c.id === chainId);
        if (!chain) {
          throw new SwitchChainError(new Error("Chain not configured"));
        }
        try {
          yield provider.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: numberToHex(chainId) }]
          });
        } catch (error) {
          const rpcError = error;
          if (rpcError.code === 4902) {
            const _b = (_a2 = chain.blockExplorers) != null ? _a2 : {}, { default: blockExplorer } = _b, blockExplorers = __objRest(_b, ["default"]);
            const blockExplorerUrls = blockExplorer ? [blockExplorer.url, ...Object.values(blockExplorers).map((explorer) => explorer.url)] : void 0;
            yield provider.request({
              method: "wallet_addEthereumChain",
              params: [
                {
                  chainId: numberToHex(chainId),
                  chainName: chain.name,
                  nativeCurrency: chain.nativeCurrency,
                  rpcUrls: [(_d = (_c = chain.rpcUrls.default) == null ? void 0 : _c.http[0]) != null ? _d : ""],
                  blockExplorerUrls
                }
              ]
            });
          } else if (rpcError.code === UserRejectedRequestError.code) {
            throw new UserRejectedRequestError(rpcError);
          } else {
            throw new SwitchChainError(rpcError);
          }
        }
        const currentChainId = yield waitForChainId(provider, chainId);
        if (currentChainId !== chainId) {
          throw new SwitchChainError(new Error("Failed to switch chain"));
        }
        return chain;
      });
    },
    onAccountsChanged(accounts) {
      const normalizedAccounts = accounts.map((account) => getAddress(account));
      config.emitter.emit("change", {
        accounts: normalizedAccounts
      });
    },
    onChainChanged(chainId) {
      return __async(this, null, function* () {
        const accounts = yield this.getAccounts();
        config.emitter.emit("change", {
          chainId: normalizeChainId(chainId),
          accounts
        });
      });
    },
    onDisconnect() {
      resetProvider();
      config.emitter.emit("disconnect");
    }
  }));
}

export { APOLLO_WALLET_RDNS, ApolloWalletNotFoundError, apolloWallet, getApolloWalletProvider, isApolloWalletInstalled, waitForApolloWalletProvider };
